import log from 'loglevel';
import { articleType } from 'src/constants/helpscoutArticles';
import { Featured } from 'src/types';
import linkUtils, { getCommunityLogoUrl } from 'src/utils/linkUtils';
import { getBranch } from './cookie';
import { getLocalStorageItem, setLocalStorageItem, Key } from './localStorage';

export const featuredPostJsFunctionRegex = /^js:(.+?)(\?(.*))?$/;

export const formatFeaturedPosts = (featuredPosts: Featured[]) => {
  return featuredPosts
    .filter(post => !post.branch || post.branch === getBranch())
    .map(post => ({
      ...post,
      communityLogoImageMedium: post.communityLogoImageMedium || getCommunityLogoUrl(post.logoImageHash),
    }));
};

export const formatFeaturedPostsForSinglePost = (featuredPosts: Featured[]) => {
  const huFeaturedPosts = featuredPosts
    .filter(post => post.featuredByCommunityName === 'HealthUnlocked');
  const firstCommunityFeaturedPost = featuredPosts
    .find(post => post.featuredByCommunityName !== 'HealthUnlocked');

  const secondFeaturedPost = firstCommunityFeaturedPost || huFeaturedPosts[1];

  return [huFeaturedPosts[0], secondFeaturedPost]
    .filter(x => !!x);
};

export const getDismissedFeaturedPostIds = () => {
  return JSON.parse(getLocalStorageItem(Key.featuredPostsDismissed) || '[]');
};

export const combinePostsWithFeaturedPosts = (posts: any, featuredPosts: Featured[]) => {
  const filteredFeaturedPosts = [...featuredPosts];
  if (filteredFeaturedPosts.length == 0) return posts;

  const insertFeaturedIndices = [1, 4];
  const combined = [];

  for (let i = 0; i < posts.length; i += 1) {
    if (insertFeaturedIndices.includes(i)) {
      combined.push(filteredFeaturedPosts.shift());
    }
    combined.push(posts[i]);
  }

  return combined;
};

export const addToDismissList = (featuredPostId: number) => {
  const dismissed = JSON.parse(getLocalStorageItem(Key.featuredPostsDismissed) || '[]');
  dismissed.push(featuredPostId);
  setLocalStorageItem(Key.featuredPostsDismissed, JSON.stringify(dismissed));
};

export const isUniqueImpression = (featuredPostId: number) => {
  const uniqueImpressions = JSON.parse(getLocalStorageItem(Key.featuredPostUniqueImpressions) || '[]');
  return uniqueImpressions.indexOf(featuredPostId) === -1;
};

export const addToUniqueImpressionsList = (featuredPostId: number) => {
  const uniqueImpressions = JSON.parse(getLocalStorageItem(Key.featuredPostUniqueImpressions) || '[]');
  uniqueImpressions.push(featuredPostId);
  setLocalStorageItem(Key.featuredPostUniqueImpressions, JSON.stringify(uniqueImpressions));
};

export const getModalParams = (url: string): any | null => {
  const query = url.split('?')[1];
  if (!query) return null;
  const params = (/^[?#]/.test(query) ? query.slice(1) : query)
    .split('&')
    .reduce((params: any, param) => {
      const [key, value] = param.split('=');
      params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
      return params;
    }, {});
    if (Object.keys(params).length > 0) {
      return params;
    } else {
      return null;
    }
};

export const getHelpScoutArticle = (url: string, featuredPostId: number): articleType | null => {
    const article = url.split('=')[1];
    if (article) {
      return article as articleType;
    } else {
      log.error(`Featured Post ${featuredPostId} URL error: ${url}`);
      return null;
    }
};

export const formatLink = (url: string): string => {
  return getCtaType(url) === 'link' ? linkUtils.prepLink(url) : '';
};

const getFunctionName = (callToActionUrl: string) => {
  const matches = featuredPostJsFunctionRegex.exec(callToActionUrl);
  const functionName = Array.isArray(matches) && matches[1];
  return functionName;
};

const isModalCta = (url: string): boolean => { // js:openModal?popup=${popupId}
  return (url.startsWith('js:') && getFunctionName(url) === 'openModal');
};

const isHelpScoutCta = (url: string): boolean => { // js:helpscout?article=${articleId || articleSlug}
  return (url.startsWith('js:') && getFunctionName(url) === 'helpscout');
};

const isMailtoCta = (url: string): boolean => { // mailto:${email}
  return url.startsWith('mailto:');
};

const isOldModalCta = (url: string): boolean => { // http://www.openmod.al
  return url.startsWith('http://www.openmod.al');
};

type CtaType = 'modal' | 'helpscout' | 'mailto' | 'oldModal' | 'link';

export const getCtaType = (url: string): CtaType => {
  if (isModalCta(url)) {
    return 'modal';
  } else if (isHelpScoutCta(url)) {
    return 'helpscout';
  } else if (isMailtoCta(url)) {
    return 'mailto';
  } else if (isOldModalCta(url)) {
    return 'oldModal';
  } else {
    return 'link';
  }
};
